import { Controller } from '@hotwired/stimulus';

// Contrôleur des avertissements vifs des paiements directs sous-traitants.
//
// Affiche EN DIRECT, pendant la frappe, DEUX dépassements distincts :
//
//   1. PAR LIGNE — « le cumul payé en direct dépasse le plafond agréé ».
//      Avant, le message n'apparaissait qu'après sauvegarde puis réouverture de
//      la situation : à la création, on pouvait dépasser sans rien voir (revue
//      Nicolas, 2026-07-29).
//   2. POUR LA SECTION — « la ventilation dépasse le montant HT de la
//      situation ». Ajouté le 2026-08-04 sur retour QA de Charles : « quand je
//      rentre un montant de paiement direct supérieur au montant HT de la
//      situation, il ne se passe rien en dynamique ».
//      Variante SITUATION NÉGATIVE (P5, plan 2026-10-30-situation-negative) :
//      quand le HT de la période est < 0, le serveur refuse TOUT paiement
//      direct (le titulaire porte tout l'avoir). L'encadré affiche alors le
//      message P5, rendu par le serveur dans `data-negative-template`, et non
//      le gabarit « ne peut pas dépasser … (-500,00 €) », qui chiffrerait un
//      plafond négatif dénué de sens et contredirait le refus serveur.
//
// CE CONTRÔLEUR N'EST PAS UNE GARDE. Les deux gardes vivent côté serveur, dans
// app/models/concerns/work_progress_statements/financial_calculations.rb
// (`guard_subcontract_payments!`), et refusent la sauvegarde. Le JS n'est qu'un
// raccourci d'affichage : un POST direct ne le voit jamais.
//
// ── PORTÉE DE L'AXE 2, ET CE QU'IL NE FAIT TOUJOURS PAS ────────────────────
//
// Le plafond serveur est `min(montant HT de la période, net à payer)`. Le JS ne
// connaît QUE la première borne, et c'est délibéré (décision D-5, révisée le
// 2026-08-04 sur le seul axe HT) :
//
//   * le MONTANT HT est un opérande VISIBLE À L'ÉCRAN — c'est le champ « Montant
//     HT de la situation » du même formulaire, que `wps-form` tient à jour même
//     en mode multi-factures (il y écrit la somme des lignes). Le comparer ne
//     crée aucune seconde source de vérité : on lit ce que l'architecte voit ;
//   * le NET À PAYER reste SERVEUR. Le reproduire exigerait de recalculer en JS
//     `HT + TVA − RG − acompte − prorata + régularisations`, soit les dix étapes
//     de `calculate_amounts`, la configuration du marché, celle du compte
//     prorata du chantier et l'historique des situations validées. Toute valeur
//     figée dans un attribut serait périmée dès la première édition.
//
// Conséquence ASSUMÉE : quand c'est le net à payer qui borne (pénalité TTC,
// déduction d'acompte, prorata), l'avertissement vif reste muet et seul le
// serveur refuse. C'est le comportement d'avant ce lot, pas une régression.
//
// ── DEUX LECTEURS DE MONTANT DANS CE FICHIER, ET C'EST VOULU ───────────────
//
// `#toCents` (axe 1) normalise « blancs + virgule », ce qui est du CODE MORT sur
// un `<input type="number">` — le navigateur normalise avant que le JS ne voie
// quoi que ce soit. Il est CONSERVÉ tel quel parce que
// `dgd_allocation_controller.js` le cite nommément comme référence de sa propre
// copie : le réécrire rendrait ce commentaire faux sans rien gagner.
// L'axe 2, lui, suit la convention actuelle du dépôt
// (`subcontract_agreed_amount_controller.js`) : `valueAsNumber` et un traitement
// EXPLICITE de `validity.badInput`. Les deux lecteurs s'accordent sur toute
// saisie atteignable ; ils divergent sur le seul `badInput`, où `#toCents` rend
// 0 et où l'axe 2 préfère NE RIEN TOUCHER — masquer un avertissement portant sur
// une valeur encore visible à l'écran est le défaut que la convention corrige.
//
// Chaque ligne porte son plafond et son déjà-payé EN CENTIMES : on compare des
// entiers, jamais des flottants, pour qu'un cumul à l'euro près ne bascule pas
// sur une erreur d'arrondi binaire.
//
// ── LE TOTAL SOUS LA BANDE (plan 2026-10-30-sous-traitants-a-13, décision 4) ──
//
// Cibles optionnelles `totalPaid` et `totalPeriodHt` : deux `<span>` qui ne
// portent QUE le montant (le libellé reste dans la vue, aucune phrase ici).
// Écrites dans `#refresh`, AVANT `#validateVentilation` et hors de ses retours
// anticipés (RV-PM2) : sans ST saisi, avec un champ en `badInput` ou devant un
// message serveur, l'axe 2 sort tôt, alors que le total doit suivre quand même.
// La somme est celle de l'axe 2 (`#ventilatedCents`, centimes entiers, P10), au
// format de `number_to_currency` (`#formatLikeServer`).
//
// ── OUVERTURE DU GROUPE REPLIÉ (P1, RV-PM3) ────────────────────────────────────
//
// La section vit dans un `Ui::ListGroupComponent` replié par `disclosure-group`
// (même élément). Un champ `min="0"` invalide dans la liste `hidden` bloque
// l'envoi SANS que le navigateur puisse le focaliser : l'architecte clique et
// rien ne se passe. D'où un écouteur `invalid` EN CAPTURE (l'événement ne bulle
// pas) sur l'élément, qui clique le déclencheur `aria-expanded="false"` quand
// la cible est dans une ligne ST. `disclosure-group#toggle` retire `hidden` de
// façon synchrone, avant que le navigateur cherche le champ à focaliser.
// C'est la SEULE ouverture : une frappe dans une ligne ST suppose la liste déjà
// visible (un champ d'une `<ul hidden>` ne reçoit ni focus ni saisie). Jamais
// depuis le champ HT, l'événement des lignes de facture ou `connect` : un
// plafond abaissé sous le déjà-payé affiche sa boîte à chaque rafraîchissement,
// et la section se rouvrirait à chaque frappe ailleurs (QA P1-07).
export default class extends Controller {
  static targets = [
    'row',
    'input',
    'errorBox',
    'errorMessage',
    'serverAlert',
    'ventilationBox',
    'ventilationMessage',
    'totalPaid',
    'totalPeriodHt',
  ];

  // Le NOM du champ « Montant HT de la situation », descendu par la vue plutôt
  // que figé ici : c'est le serveur qui le fabrique (`form.field_name`), et un
  // sélecteur recopié à la main dériverait en silence le jour où le champ change
  // de nom — l'avertissement disparaîtrait sans la moindre erreur.
  static values = { htField: String };

  connect() {
    this.#bindPeriodHtField();

    // Le mode multi-factures n'émet PAS d'événement `input` : `wps-form` écrit
    // la somme des lignes directement dans `amountField.value`
    // (wps_form_controller.js#onTotalChanged), une écriture programmatique
    // qu'aucun listener `input` ne voit. L'événement de la table de lignes est
    // donc le SEUL signal disponible sur ce mode.
    // ⚠️ `detail.initial` DISCRIMINE, et l'ignorer est un défaut silencieux
    // mesuré en navigateur : `invoice_lines_table_controller.js` émet cet
    // événement UNE FOIS AU CHARGEMENT, avec `initial: true`. Traité comme une
    // frappe, il déclenchait `#clearServerAlert()` sur le rendu HTML complet du
    // refus `/edit` — le message serveur était effacé dans la milliseconde et
    // l'architecte se retrouvait devant un formulaire muet qui refusait
    // d'enregistrer. Le chemin `/new` y échappait (son `turbo_stream` ne re-rend
    // pas la table de lignes), donc aucun test serveur ne pouvait le voir.
    this.onLinesTotalChanged = (event) => this.#refresh(!event?.detail?.initial);
    window.addEventListener('invoice-lines:total-changed', this.onLinesTotalChanged);

    // LE RELAIS DANS L'AUTRE SENS, et il n'est pas décoratif. Au retour d'un
    // refus, le serveur remplit le conteneur d'alerte par `turbo_stream.update`
    // — mais la SECTION, elle, n'est pas re-rendue, donc `connect()` ne rejoue
    // pas. Sans cet observateur, l'encadré vif resterait affiché À CÔTÉ du
    // message serveur : deux cadres rouges portant la même phrase, c'est-à-dire
    // très exactement le doublon que ce lot supprime, re-créé dans la scène qui
    // l'a fait remonter.
    //
    // Un `MutationObserver` sur ce seul conteneur plutôt qu'un listener global
    // `turbo:before-stream-render` : la portée est locale, la désinscription
    // triviale, et l'ordre de rendu de Turbo n'a pas à être supposé. Pas de
    // boucle possible — il ne réagit qu'à un conteneur NON VIDE et ne touche
    // jamais qu'à l'encadré vif.
    this.serverAlertObserver = new MutationObserver(() => {
      if (this.#hasServerAlert()) this.#applyVentilationState(null);
    });
    if (this.hasServerAlertTarget) {
      this.serverAlertObserver.observe(this.serverAlertTarget, { childList: true });
    }

    // Capture obligatoire : `invalid` ne bulle pas. Retiré en `disconnect` avec
    // la même phase, sinon le DOM ne retire rien.
    this.onInvalid = (event) => {
      if (this.#rowOf(event.target)) this.#openCollapsedGroup();
    };
    this.element.addEventListener('invalid', this.onInvalid, true);

    // Recalcule dès l'affichage : les boîtes rendues par le serveur reflètent la
    // donnée PERSISTÉE, or les champs peuvent déjà porter d'autres valeurs après
    // un re-rendu 422. Une seule source, celle visible à l'écran. `false` : rien
    // n'a été touché, donc un message serveur présent garde la main.
    this.#refresh(false);
  }

  disconnect() {
    this.serverAlertObserver?.disconnect();
    window.removeEventListener('invoice-lines:total-changed', this.onLinesTotalChanged);
    this.element.removeEventListener('invalid', this.onInvalid, true);

    if (this.boundPeriodHtField && this.onPeriodHtInput) {
      this.boundPeriodHtField.removeEventListener('input', this.onPeriodHtInput);
      this.boundPeriodHtField = null;
    }
  }

  validate() {
    this.#refresh(true);
  }

  // N'ouvre JAMAIS le groupe (RV-PM3) : seul l'écouteur `invalid` le fait.
  #refresh(fromUserInput) {
    this.rowTargets.forEach((row) => this.#validateRow(row));
    this.#writeTotals();
    this.#validateVentilation(fromUserInput);
  }

  #writeTotals() {
    if (this.hasTotalPaidTarget) {
      this.totalPaidTarget.textContent = this.#formatLikeServer(this.#ventilatedCents());
    }
    if (!this.hasTotalPeriodHtTarget) return;

    // Même convention que l'axe 2 : sur `badInput` l'architecte voit encore un
    // montant, on ne touche à rien. Un champ vide est une période à zéro.
    const field = this.#periodHtField();
    if (!field || field.validity.badInput) return;

    this.totalPeriodHtTarget.textContent = this.#formatLikeServer(this.#periodHtCents(field) ?? 0);
  }

  // La ligne ST (`<tbody>` cible `row`) qui contient `node`, dans CET élément.
  #rowOf(node) {
    const row = node?.closest?.('[data-subcontract-cap-target~="row"]');

    return row && this.element.contains(row) ? row : null;
  }

  // Le déclencheur du `Ui::ListGroupComponent`, seulement s'il est replié :
  // cliquer un groupe déjà ouvert le REFERMERAIT (QA P1-04).
  #openCollapsedGroup() {
    this.element
      .querySelector('[data-disclosure-group-target~="item"][aria-expanded="false"]')
      ?.click();
  }

  // Le champ « Montant HT » est un FRÈRE de la section, hors du périmètre du
  // contrôleur : Stimulus ne résout les `data-action` que vers les ancêtres,
  // donc une frappe dedans ne rejouerait jamais `validate`. D'où un listener
  // posé à la main — et retiré en `disconnect`, symétrie exigée par
  // app/javascript/controllers/CLAUDE.md.
  #bindPeriodHtField() {
    const field = this.#periodHtField();
    if (!field) return;

    // Nom DISTINCT de la méthode `#periodHtField()` : cette référence n'existe
    // que pour désinscrire le listener, et les lectures passent toutes par la
    // méthode, qui re-résout le champ à chaque appel.
    this.boundPeriodHtField = field;
    this.onPeriodHtInput = () => this.#refresh(true);
    field.addEventListener('input', this.onPeriodHtInput);
  }

  #periodHtField() {
    if (!this.hasHtFieldValue || this.htFieldValue === '') return null;

    const form = this.element.closest('form');
    if (!form) return null;

    return form.querySelector(`input[name="${this.htFieldValue}"]`);
  }

  #validateRow(row) {
    const input = row.querySelector('[data-subcontract-cap-target="input"]');
    const box = row.querySelector('[data-subcontract-cap-target="errorBox"]');
    const message = row.querySelector('[data-subcontract-cap-target="errorMessage"]');
    if (!input || !box || !message) return;

    const capCents = parseInt(input.dataset.capCents, 10);
    const paidCents = parseInt(input.dataset.paidCents, 10);
    if (Number.isNaN(capCents) || Number.isNaN(paidCents)) return;

    const enteredCents = this.#toCents(input.value);
    const projectedCents = paidCents + enteredCents;

    if (projectedCents > capCents) {
      message.textContent =
        `Le cumul payé en direct (${this.#format(projectedCents)}) dépasse le plafond agréé ` +
        `(${this.#format(capCents)}). Relevez le plafond sur la fiche du sous-traitant ` +
        `pour enregistrer ce paiement.`;
      box.classList.remove('hidden');
    } else {
      message.textContent = '';
      box.classList.add('hidden');
    }
  }

  #validateVentilation(fromUserInput) {
    if (!this.hasVentilationBoxTarget || !this.hasVentilationMessageTarget) return;

    // Saisie invalide quelque part : on ne sait plus ce qu'on compare, donc on
    // NE TOUCHE À RIEN. Le COLLAGE de « 150 000 » rend `value === ""` alors que
    // l'utilisateur voit un montant : masquer ici effacerait un avertissement
    // portant sur une valeur encore à l'écran.
    const field = this.#periodHtField();
    if (field?.validity.badInput) return;
    if (this.inputTargets.some((input) => input.validity.badInput)) return;

    // Le message du SERVEUR possède la section tant que l'architecte n'a rien
    // touché : il vient d'être rendu, et il est plus complet que l'axe HT (il
    // connaît aussi le net à payer). Dès la première frappe il est PÉRIMÉ — son
    // opérande vient de bouger — donc on le retire et l'axe vif prend la main.
    // Sans ce relais, les deux cadres coexisteraient et l'on retomberait sur le
    // doublon signalé en QA.
    if (!fromUserInput && this.#hasServerAlert()) {
      this.#applyVentilationState(null);
      return;
    }
    if (fromUserInput) this.#clearServerAlert();

    const ventilatedCents = this.#ventilatedCents();

    // MIROIR EXACT du gate serveur `return unless subcontract_total.positive?`
    // (`financial_calculations.rb#guard_subcontract_payments!`) : sans rien de
    // ventilé, la garde ne s'arme jamais, donc l'avertissement qui l'annonce ne
    // doit pas s'armer non plus.
    //
    // Il n'est pas théorique. Le champ « Montant HT » porte `min="0"`, mais cet
    // attribut n'empêche PAS de taper « -100 » — il ne fait que rendre le champ
    // `:invalid`, et `validity.badInput` reste FAUX puisque la saisie est un
    // nombre bien formé. Sans ce gate, `0 > -10000` est vrai et l'encadré
    // annonçait « la ventilation ne peut pas dépasser le montant HT de cette
    // situation (-100,00 €) » alors qu'aucun sous-traitant n'est payé : un
    // message faux, et désaccordé du serveur qu'il est censé anticiper.
    if (ventilatedCents <= 0) {
      this.#applyVentilationState(null);
      return;
    }

    const ceilingCents = this.#periodHtCents(field);
    if (ceilingCents === null) {
      this.#applyVentilationState(null);
      return;
    }

    // Situation négative (P5) : aucun paiement direct n'est permis, quel qu'en
    // soit le montant. On annonce le refus serveur, jamais un plafond négatif.
    if (ceilingCents < 0) {
      this.#applyVentilationState({ negative: true });
      return;
    }

    // Comparateur STRICT : à l'égalité le sous-traitant a tout réalisé et le
    // titulaire ne perçoit plus que la TVA. Le serveur accepte ce cas, crier
    // dessus userait l'avertissement.
    this.#applyVentilationState(
      ventilatedCents > ceilingCents ? { ventilatedCents, ceilingCents } : null
    );
  }

  // Garde d'ÉTAT sur le tuple, et jamais sur la chaîne rendue : le serveur
  // formate avec des espaces ASCII et `Intl` avec U+202F/U+00A0, donc une
  // comparaison de chaînes ne court-circuiterait jamais. Sans elle, la
  // live-region ré-annoncerait à chaque frappe.
  #applyVentilationState(state) {
    let signature = 'off';
    if (state?.negative) signature = 'negative';
    else if (state !== null) signature = `${state.ventilatedCents}/${state.ceilingCents}`;
    if (signature === this.lastVentilationSignature) return;

    this.lastVentilationSignature = signature;

    if (state === null) {
      this.ventilationMessageTarget.textContent = '';
      this.ventilationBoxTarget.classList.add('hidden');
      return;
    }

    // Les gabarits descendent en `data-template` / `data-negative-template`
    // depuis la vue : aucune phrase française ne vit dans un `.js`, fichier que
    // ni `ui-lint-erb.sh` ni `scripts/lint-terminology.sh` ne lisent. Toujours
    // par `textContent`, jamais en HTML.
    const { dataset } = this.ventilationMessageTarget;
    this.ventilationMessageTarget.textContent = state.negative
      ? dataset.negativeTemplate || ''
      : (dataset.template || '').replace('%<ceiling>s', this.#format(state.ceilingCents));
    this.ventilationBoxTarget.classList.remove('hidden');
  }

  // `null` = « aucun plafond connu » (champ absent ou vide), et non « plafond à
  // zéro » : sans montant de situation saisi, il n'y a rien à comparer et
  // l'avertissement doit rester muet.
  #periodHtCents(field) {
    if (!field) return null;

    const value = field.valueAsNumber;

    return Number.isNaN(value) ? null : Math.round(value * 100);
  }

  // UNE somme pour l'axe 2 et pour le total sous la bande : entiers en
  // centimes, jamais `0.1 + 0.2` (P10). Un champ en `badInput` y vaut 0 : le
  // total affiche alors la somme des seuls champs valides (QA ST-03), l'axe 2
  // l'intercepte avant de comparer.
  #ventilatedCents() {
    return this.inputTargets.reduce((sum, input) => sum + this.#enteredCents(input), 0);
  }

  // Un champ vide vaut 0 dans la SOMME — c'est un sous-traitant non payé sur la
  // période, pas une inconnue. Le cas réellement inconnu (`badInput`) est
  // intercepté en amont.
  #enteredCents(input) {
    const value = input.valueAsNumber;

    return Number.isNaN(value) ? 0 : Math.round(value * 100);
  }

  #hasServerAlert() {
    return this.hasServerAlertTarget && this.serverAlertTarget.childElementCount > 0;
  }

  #clearServerAlert() {
    if (!this.#hasServerAlert()) return;

    this.serverAlertTarget.replaceChildren();
  }

  // « 11 000,50 » (copier-coller, espace insécable) doit donner 1100050 et non
  // 11 : on retire TOUS les blancs avant de normaliser la virgule. Même
  // normalisation que Subcontracts::AmountParser.call côté serveur.
  #toCents(rawValue) {
    const normalized = String(rawValue || '')
      .replace(/\s/g, '')
      .replace(',', '.');
    const parsed = parseFloat(normalized);

    return Number.isNaN(parsed) ? 0 : Math.round(parsed * 100);
  }

  #format(cents) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  }

  // Le total est rendu d'abord par `number_to_currency`, dont le délimiteur et
  // l'espace avant « € » sont des espaces ASCII (`config/locales/fr.yml`),
  // quand `Intl` pose U+202F et U+00A0. Sans cet alignement, le texte
  // changerait d'octets au premier rafraîchissement (dès `connect`) pour un
  // montant identique.
  #formatLikeServer(cents) {
    return this.#format(cents).replace(/[\u00A0\u202F]/g, ' ');
  }
}
